import { api } from "@/lib/api/client";
import type {
  CreateProducerManualPayload,
  CreateProducerManualResponse,
  EventTicketTypesResponse,
  ProducerDetailResponse,
  ProducerListResponse,
  ProducerListStatus,
  SetCommissionPayload,
  UpdateProducerPayload,
} from "./types";

const BASE = "/api/nexus/producers";

export const producersMgmtApi = {
  list: async (params: {
    q?: string;
    status?: ProducerListStatus | "";
    page?: number;
    pageSize?: number;
  }): Promise<ProducerListResponse> => {
    const { data } = await api.get<ProducerListResponse>(BASE, { params });
    return data;
  },

  // Alta manual directa: crea o promueve la cuenta a productor + perfil de
  // catálogo. El productor solo tiene que entrar al Hub con su cuenta.
  createManual: async (
    payload: CreateProducerManualPayload,
  ): Promise<CreateProducerManualResponse> => {
    const { data } = await api.post<CreateProducerManualResponse>(BASE, payload);
    return data;
  },

  get: async (id: number): Promise<ProducerDetailResponse> => {
    const { data } = await api.get<ProducerDetailResponse>(`${BASE}/${id}`);
    return data;
  },

  update: async (id: number, payload: UpdateProducerPayload): Promise<void> => {
    await api.patch(`${BASE}/${id}`, payload);
  },

  softDelete: async (id: number): Promise<void> => {
    await api.post(`${BASE}/${id}/soft-delete`);
  },

  restore: async (id: number): Promise<void> => {
    await api.post(`${BASE}/${id}/restore`);
  },

  hardDelete: async (id: number, confirmEmail: string, force = false): Promise<void> => {
    await api.post(`${BASE}/${id}/hard-delete`, { confirmEmail, force });
  },

  hardDeleteClub: async (clubId: number, confirmName: string, force = false): Promise<void> => {
    await api.post(`${BASE}/clubs/${clubId}/hard-delete`, { confirmName, force });
  },

  hardDeleteEvent: async (eventId: number, confirmTitle: string, force = false): Promise<void> => {
    await api.post(`${BASE}/events/${eventId}/hard-delete`, { confirmTitle, force });
  },

  removeStaff: async (contextRoleId: number): Promise<void> => {
    await api.delete(`${BASE}/staff/${contextRoleId}`);
  },

  // Comisión Mixcore negociada. Pasar { type: null, value: null } resetea
  // al nivel superior (club → default).
  setClubCommission: async (clubId: number, payload: SetCommissionPayload): Promise<void> => {
    await api.put(`${BASE}/clubs/${clubId}/commission`, payload);
  },

  setEventCommission: async (eventId: number, payload: SetCommissionPayload): Promise<void> => {
    await api.put(`${BASE}/events/${eventId}/commission`, payload);
  },

  // Split Payment por-evento. enabled=true exige que el productor tenga MP
  // conectada (sino el backend responde 400 PRODUCER_NO_MP).
  setEventSplitEnabled: async (eventId: number, enabled: boolean): Promise<void> => {
    await api.put(`${BASE}/events/${eventId}/split`, { enabled });
  },

  // Entradas de un evento: listar + marcar/desmarcar agotado.
  getEventTicketTypes: async (eventId: number): Promise<EventTicketTypesResponse> => {
    const { data } = await api.get<EventTicketTypesResponse>(`${BASE}/events/${eventId}/ticket-types`);
    return data;
  },

  setTicketTypeSoldOut: async (id: number, soldOut: boolean): Promise<void> => {
    await api.put(`${BASE}/ticket-types/${id}/sold-out`, { soldOut });
  },

  // Hub handoff: acuña un token de un solo uso para saltar al Hub logueado
  // como el propio admin Nexus (super-usuario). El backend lo emite SIEMPRE
  // para el usuario autenticado — no se le pasa userId.
  hubHandoffToken: async (): Promise<{ token: string; expiresIn: number }> => {
    const { data } = await api.post<{ token: string; expiresIn: number }>(
      "/api/nexus/hub-handoff"
    );
    return data;
  },
};
